import { getODAnonymousId } from '@/services/segment';

import { isSSR } from './isSSR';
import { logger } from './logger';

interface Window {
  Outdoorsy?: {
    metrics?: { getIdentifier?: () => { did: string; sid: string } };
  };
}

const originOf = (url?: string) => {
  if (!url) return undefined;
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
};

// apiRequest also calls third parties (NPS, Mapbox, WordPress, Roamly), which must never see the key.
const isCoreApiUrl = (url?: string) => {
  const origin = originOf(url);
  if (!origin) return false;
  return [process.env.internalCoreApi, process.env.coreApiHost].some(
    base => originOf(base) === origin,
  );
};

export const getHeaders = async (url?: string) => {
  const headers: Record<string, unknown> = {};

  const partnerId = process.env.partnerId;
  if (partnerId) {
    headers['Partner-ID'] = partnerId;
  }
  if (isSSR()) {
    // Not in next.config.js env: that inlines values into the client bundle.
    const ssrRateLimitKey = process.env.RENTER_SSR_RATE_LIMIT_KEY;
    if (ssrRateLimitKey && isCoreApiUrl(url)) {
      headers['X-Renter-Ssr-Key'] = ssrRateLimitKey;
    }
  }
  const getIdentifier = !isSSR() && (window as Window)?.Outdoorsy?.metrics?.getIdentifier;
  if (getIdentifier) {
    const { sid, did } = getIdentifier();
    headers['X-App-Session'] = sid;
    headers['X-Device-ID'] = did;
    headers['riskified-session-id'] = window?.sessionStorage?.getItem('riskified-session-id');
  }
  if (!isSSR()) {
    try {
      const anonId = await getODAnonymousId();
      if (anonId) {
        headers['X-Anonymous-ID'] = anonId;
      }
    } catch (err) {
      logger.error(err);
    }
  }

  return headers;
};
